import React, { useCallback, useEffect, useRef, useState } from 'react';
import { CircularProgress, Dialog } from '@material-ui/core';
import { IdaEntityType, IdaGenerateMode, useIdaGenerateMutation } from '../../../../graphql/types';
import IdaMascot from '../../../../images/ida-mascot.png';
import { getUserFacingErrorText } from '../../../common/utilities/userFacingError';

export const IDA_SUGGEST_FAILED = 'IDA could not write a suggestion right now. Click Re-generate to try again. You can also type the text yourself.';

// CMP Accelerator Member Dashboard (2) IDA pop-up (ACC:1222-1262, logic ACC:2405-2490):
// Generate opens the suggestion straight away; "Use" applies the (editable) text,
// "Re-generate" asks again. With nothing to go on, the design "A Few Details Needed"
// pop-up shows instead. CPA pages keep the older IDAGenerateModal.

const GOLD = '#E1A731';
const GOLD_DARK = '#C6912A';
const GREEN = '#10B981';
const RED = '#ED1C24';

export type CmpIdaTarget = {
  mode: IdaGenerateMode;
  heading: string;
  body: string;
  warnBody: string;
  maxLength: number;
};

// Design texts (ACC:2408-2446).
export const CMP_IDA_TARGETS: Record<'company' | 'perkDescription' | 'perkTitle', CmpIdaTarget> = {
  company: {
    mode: IdaGenerateMode.CompanyDescription,
    heading: 'I Have Generated Your Company Description',
    body: 'Review the suggested description below and edit it if needed, then click "Use" to apply, or re-generate.',
    warnBody: 'Please enter a few details about your business or your website URL first. The more details you provide, the better IDA can tailor your company description.',
    maxLength: 2000,
  },
  perkDescription: {
    mode: IdaGenerateMode.PerkDescription,
    heading: 'Let Me Generate Your Perk Description',
    body: 'I generate your perk description based on the key details you entered about your perk offer. Review the suggested description below and edit it if needed, then click Use to apply it.',
    warnBody: 'Please enter a few key details about your perk offer first. The more details you provide, the better IDA can tailor your perk description.',
    maxLength: 250,
  },
  perkTitle: {
    mode: IdaGenerateMode.PerkTitle,
    heading: 'Let Me Generate Your Perk Title',
    body: 'I generate your perk title based on your perk description and the key details you entered. Review the suggested title below and edit it if needed, then click Use to apply it.',
    warnBody: 'Please enter a few key details about your offer first. The more details you provide, the better IDA can tailor your perk title.',
    maxLength: 50,
  },
};

const closeButton: React.CSSProperties = {
  position: 'absolute', top: 14, right: 14, width: 28, height: 28, borderRadius: '50%', background: RED, color: '#FFFFFF',
  border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0,
};
const actionButton = (background: string): React.CSSProperties => ({
  flex: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8, background, color: '#FFFFFF', border: 'none',
  borderRadius: 8, padding: '11px 22px', fontFamily: "'DM Sans', sans-serif", fontSize: 14, fontWeight: 600, letterSpacing: '0.04em', cursor: 'pointer',
});

export const CmpIdaSuggestDialog = ({
  open, onClose, target, entityName, keyDetails, websiteUrl, onUse,
}: {
  open: boolean;
  onClose: () => void;
  target: CmpIdaTarget;
  entityName: string;
  /** What IDA writes from: the current text and any details on the form. Empty = design warning. */
  keyDetails: string;
  websiteUrl?: string | null;
  onUse: (text: string) => void;
}) => {
  const [idaGenerate] = useIdaGenerateMutation();
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const started = useRef(false);
  const hasDetails = !!keyDetails.trim() || !!(websiteUrl || '').trim();

  const generate = useCallback(async () => {
    setBusy(true);
    setError('');
    try {
      const { data } = await idaGenerate({ variables: { input: {
        entityName: entityName || 'the company', entityType: IdaEntityType.Company, mode: target.mode,
        keyDetails: keyDetails.trim() || `Website: ${(websiteUrl || '').trim()}`, maxLength: target.maxLength,
        ...((websiteUrl || '').trim() ? { websiteUrl: (websiteUrl || '').trim() } : {}),
      } } });
      const generated = data?.idaGenerate?.success ? (data.idaGenerate.generatedText || '').trim() : '';
      if (!generated) throw new Error('IDA returned no suggestion.');
      setText(generated.slice(0, target.maxLength));
    } catch (caught) {
      setError(getUserFacingErrorText(caught, IDA_SUGGEST_FAILED));
    } finally {
      setBusy(false);
    }
  }, [entityName, idaGenerate, keyDetails, target, websiteUrl]);

  useEffect(() => {
    if (open && hasDetails && !started.current) {
      started.current = true;
      setText('');
      generate();
    }
    if (!open) started.current = false;
  }, [generate, hasDetails, open]);

  const mascot = (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
      <img src={IdaMascot} alt="IDA" style={{ height: 58, width: 'auto' }} />
      <span style={{ border: '1.5px solid #1A1A1A', borderRadius: '14px 14px 14px 2px', padding: '6px 12px', fontSize: 12, fontWeight: 600,
        color: '#000000', background: '#FFFFFF', marginTop: -6 }}>Hi,<br />I am IDA</span>
    </div>
  );

  if (!hasDetails) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth={false} PaperProps={{ style: { borderRadius: 16, width: 'min(440px, 90vw)', margin: 0 } }}>
        <div data-testid="cmp-ida-warn" style={{ padding: '28px 32px 32px', boxSizing: 'border-box', position: 'relative', display: 'flex',
          flexDirection: 'column', alignItems: 'center', gap: 16, fontFamily: "'DM Sans', sans-serif" }}>
          <button type="button" aria-label="Close" onClick={onClose} style={closeButton}>✕</button>
          <div style={{ width: 64, height: 64, borderRadius: '50%', background: GOLD, display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: 6 }}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true"><circle cx="12" cy="12" r="9" /><path d="M12 8v5M12 16h.01" /></svg>
          </div>
          <div style={{ fontSize: 16, fontWeight: 600, color: '#000000', textAlign: 'center' }}>A Few Details Needed</div>
          <div style={{ fontSize: 14, color: '#000000', textAlign: 'center', lineHeight: 1.7 }}>{target.warnBody}</div>
          <button type="button" onClick={onClose} style={{ ...actionButton(GOLD), flex: 'none', padding: '11px 32px', fontSize: 13, marginTop: 4 }}>OK</button>
        </div>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth={false}
      PaperProps={{ style: { borderRadius: 16, border: `3px solid ${GOLD}`, width: 'min(560px, 94vw)', margin: 0 } }}>
      <div data-testid="cmp-ida-suggest" style={{ padding: '24px 28px 28px', boxSizing: 'border-box', position: 'relative', display: 'flex',
        flexDirection: 'column', gap: 14, fontFamily: "'DM Sans', sans-serif" }}>
        <button type="button" aria-label="Close" onClick={onClose} style={closeButton}>✕</button>
        {mascot}
        <div style={{ fontSize: 20, fontWeight: 600, color: GOLD, lineHeight: 1.35 }}>{target.heading}</div>
        <div style={{ fontSize: 14, color: '#000000', lineHeight: 1.6 }}>{target.body}</div>
        <div style={{ position: 'relative' }}>
          <textarea rows={7} aria-label="Suggested text" value={text} maxLength={target.maxLength} disabled={busy}
            onChange={(event) => setText(event.target.value)}
            style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, lineHeight: 1.6, border: `1px solid ${GOLD}`, borderRadius: 8, padding: '14px 16px',
              outline: 'none', resize: 'vertical', color: '#000000', width: '100%', boxSizing: 'border-box' }} />
          {busy ? <CircularProgress size={28} style={{ position: 'absolute', left: '50%', top: '50%', marginLeft: -14, marginTop: -14, color: GOLD }} /> : null}
        </div>
        {error ? <div role="alert" style={{ fontSize: 13, fontWeight: 600, color: RED }}>{error}</div> : null}
        <div style={{ display: 'flex', gap: 16 }}>
          <button type="button" disabled={busy || !text.trim()} onClick={() => onUse(text.trim())}
            style={{ ...actionButton(GOLD), opacity: busy || !text.trim() ? 0.6 : 1 }}
            onMouseEnter={(event) => { event.currentTarget.style.background = GOLD_DARK; }}
            onMouseLeave={(event) => { event.currentTarget.style.background = GOLD; }}>Use</button>
          <button type="button" disabled={busy} onClick={generate} style={{ ...actionButton(GREEN), opacity: busy ? 0.6 : 1 }}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M21 12a9 9 0 11-2.6-6.4M21 3v6h-6" /></svg>
            Re-generate
          </button>
        </div>
      </div>
    </Dialog>
  );
};

export default CmpIdaSuggestDialog;
